/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateArticleSentimentV2Config } from 'state/thunks/articlesentimentv2';
import { setTempArticleSentimentConfig } from 'state/slices/articlesentimentv2'
// material
import ConfigV2Controller from 'material/expandablesection/v2/configv2controller/configv2controller'
import ExpandableSection from 'material/expandablesection/expandablesection'
import { ReactComponent as GridIcon } from 'assets/icons/grid.svg'
// content

// styles
import injectSheet from 'react-jss'
import styles from './generalsection_styles'
import EmojiPreview from 'material/emojipreview/emojipreview';
import Headline from './headline/headline';
import ContentAlignment from './contentalignment/contentalignment';
import ReactionCounts from './reactioncounts/reactioncounts';
import ReactionTooltip from './reactiontooltip/reactiontooltip';
import HeaderTitle from './headertitle/headertitle';



/* ========== ~~~~~~~~~~ ARTICLE SENTIMENT CONFIG V2: GENERAL SECTION ~~~~~~~~~~ ========== */
const GeneralSection = injectSheet(styles)((props) => {
  
  const dispatch = useDispatch()
  const { generalConfig: savedGeneralConfig, articleSentimentUnitId } = useSelector(state => state.articleSentimentV2.articleSentimentConfig)
  const { generalConfig: tempGeneralConfig } = useSelector(state => state.articleSentimentV2.tempArticleSentimentConfig)
  const { isConfigValid } = useSelector(state => state.articleSentimentV2)

  const [isUpdating, setIsUpdating] = useState(false)
  const [isOpened, setIsOpened] = useState(false)

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = useCallback(async (e) => {
    e.stopPropagation()
    setIsUpdating(true)
    const generalConfigRequest = {...tempGeneralConfig}
    const response = await dispatch(updateArticleSentimentV2Config({ data: generalConfigRequest, unitUUID: articleSentimentUnitId, section: 'general' }))
    if(response.payload) setIsOpened(false)
    setIsUpdating(false)
  }, [articleSentimentUnitId, dispatch, tempGeneralConfig])

  const handleCancel = useCallback(async() => {
    setIsOpened(false)
    dispatch(setTempArticleSentimentConfig({
      configName: 'generalConfig',
      data: savedGeneralConfig
    }))
  }, [dispatch, savedGeneralConfig])

  const isChanged = useMemo(() => {
    const isFieldsChanged = !(JSON.stringify(tempGeneralConfig) === JSON.stringify(savedGeneralConfig));
    return isFieldsChanged
  }, [tempGeneralConfig, savedGeneralConfig])


  const handleGeneralConfigChange = useCallback((newConfigValues) => {
    dispatch(setTempArticleSentimentConfig({
      configName: 'generalConfig',
      data: {
        ...tempGeneralConfig,
        ...newConfigValues
      }
    }))
}, [dispatch, tempGeneralConfig]);

  const controller = (
    <ConfigV2Controller
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating}
      disableSave={!isChanged || isConfigValid.generalConfig === false}
      saveTitle={'Save Changes'}
      icon={<GridIcon />}
      title='General'
    />
  );


  const generalConfigData = useMemo((section) => {
    return {
      header: {
        enableHeader: tempGeneralConfig.enableHeader,
        headline: tempGeneralConfig.headline,
        enableHeaderBorder: tempGeneralConfig.enableHeaderBorder,
        headerIcon: tempGeneralConfig.headerIcon
      },
      headline: {
        headline: tempGeneralConfig.headline
      },
      contentAlignment: {
        contentAlignment: tempGeneralConfig.contentAlignment
      },
      reactionCounts: {
        enableCombinedReactionCount: tempGeneralConfig.enableCombinedReactionCount,
        enableIndividualReactionCount: tempGeneralConfig.enableIndividualReactionCount
      },
      reactionTooltip: {
        enableTooltipHoverOnEmoji: tempGeneralConfig.enableTooltipHoverOnEmoji
      }
    }
  }, [tempGeneralConfig])

  return (
    <ExpandableSection
      className={`expandablesectionv2 generalsection ${props.classes.generalsection}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating}
      disableSave={!isChanged}
    >
      <HeaderTitle {...generalConfigData.header} onChange={handleGeneralConfigChange} />
      <ContentAlignment {...generalConfigData.contentAlignment} onChange={handleGeneralConfigChange} />
      <ReactionCounts {...generalConfigData.reactionCounts} onChange={handleGeneralConfigChange} />
      <ReactionTooltip {...generalConfigData.reactionTooltip} onChange={handleGeneralConfigChange} />
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default GeneralSection;